import type { Station, StationKind } from '@/state/types';

/**
 * Phase 8.2 — Modulare Wachen-Erweiterungen.
 *
 * Tier-Upgrades (Phase 4) bleiben für Slots/Unterhalt-Skalierung. Erweiterungen
 * sind eine ORTHOGONALE zweite Achse: jede Wache kann sowohl Tier 3 sein als
 * auch z.B. eine Werkstatt + Rettungswachenanbau besitzen.
 *
 * `effectiveVehicleSlots(station) = currentTier.vehicleSlots + sum(extensions[].vehicleSlots)`
 */

export interface ExtensionEffects {
  /** Adds vehicle slots on top of the tier-defined slots. */
  vehicleSlots?: number;
  /** Workshop in this station — repair available locally. */
  allowsRepair?: boolean;
  /** EMS vehicles (RTW/KTW/NEF) can be stationed here. */
  allowsEmsVehicles?: boolean;
  /** Patient capacity bonus for hospitals. */
  patientCapacity?: number;
  // Phase 10.1.3 — police-specific flags
  /** Adds N holding-cell slots for arrested persons. */
  holdingCells?: number;
  /** Allows installation of `police-dog` modules. */
  allowsPoliceK9?: boolean;
  /** Allows stationing of WaWe vehicles. */
  allowsWaterCannon?: boolean;
  /** Allows stationing of police helicopters. */
  allowsPoliceHelo?: boolean;
  /** Multiplier reduction on SEK alarm duration (0..1). */
  sekAlarmReduction?: number;
}

export interface StationExtension {
  id: string;
  name: string;
  description: string;
  cost: number;
  upkeepPerDay: number;
  /** Build time in real-time hours (uses BUILD_TIME_REAL_RATIO). */
  buildTimeHours: number;
  effects: ExtensionEffects;
}

/** Per-vehicle-station-kind catalog. Hospitals get their own list below. */
const FF_EXTENSIONS: StationExtension[] = [
  {
    id: 'ff-vehicle-hall',
    name: 'Fahrzeughalle',
    description: 'Erweitert die Garage um 2 zusätzliche Stellplätze.',
    cost: 50_000,
    upkeepPerDay: 300,
    buildTimeHours: 4,
    effects: { vehicleSlots: 2 },
  },
  {
    id: 'ff-workshop',
    name: 'Werkstatt',
    description: 'Reparaturen können an dieser Wache durchgeführt werden.',
    cost: 30_000,
    upkeepPerDay: 750,
    buildTimeHours: 6,
    effects: { allowsRepair: true },
  },
  {
    id: 'ff-ems-extension',
    name: 'Rettungswachenanbau',
    description:
      'Ermöglicht die Unterbringung von 2 zusätzlichen Rettungsfahrzeugen (RTW/KTW/NEF).',
    cost: 150_000,
    upkeepPerDay: 1_000,
    buildTimeHours: 5 / 60, // 5 min in Wiki-Zeit
    effects: { allowsEmsVehicles: true, vehicleSlots: 2 },
  },
];

const BF_EXTENSIONS: StationExtension[] = [
  {
    id: 'bf-vehicle-hall',
    name: 'Großfahrzeughalle',
    description: 'Erweitert die Garage um 4 zusätzliche Stellplätze.',
    cost: 120_000,
    upkeepPerDay: 600,
    buildTimeHours: 8,
    effects: { vehicleSlots: 4 },
  },
  {
    id: 'bf-workshop',
    name: 'Werkstatt',
    description: 'Reparaturen können an dieser Wache durchgeführt werden.',
    cost: 80_000,
    upkeepPerDay: 1_200,
    buildTimeHours: 12,
    effects: { allowsRepair: true },
  },
  {
    id: 'bf-ems-extension',
    name: 'Rettungswachenanbau',
    description:
      'Ermöglicht die Unterbringung von 4 zusätzlichen Rettungsfahrzeugen (RTW/KTW/NEF).',
    cost: 250_000,
    upkeepPerDay: 1_500,
    buildTimeHours: 1,
    effects: { allowsEmsVehicles: true, vehicleSlots: 4 },
  },
  {
    id: 'bf-training-ground',
    name: 'Übungsgelände',
    description: 'Beschleunigt langfristig die Eskalations-Reaktion (kosmetisch).',
    cost: 200_000,
    upkeepPerDay: 1_000,
    buildTimeHours: 24,
    effects: {},
  },
];

const EMS_EXTENSIONS: StationExtension[] = [
  {
    id: 'ems-vehicle-hall',
    name: 'Fahrzeughalle',
    description: 'Erweitert die Garage um 2 zusätzliche RTW/KTW/NEF-Stellplätze.',
    cost: 60_000,
    upkeepPerDay: 350,
    buildTimeHours: 5,
    effects: { vehicleSlots: 2 },
  },
  {
    id: 'ems-workshop',
    name: 'Werkstatt',
    description: 'Reparaturen können an dieser Wache durchgeführt werden.',
    cost: 40_000,
    upkeepPerDay: 800,
    buildTimeHours: 8,
    effects: { allowsRepair: true },
  },
];

const HOSPITAL_EXTENSIONS: StationExtension[] = [
  {
    id: 'hosp-er-extension',
    name: 'Erweiterte Notaufnahme',
    description: 'Erhöht die Patientenkapazität um 2.',
    cost: 200_000,
    upkeepPerDay: 8_000,
    buildTimeHours: 6,
    effects: { patientCapacity: 2 },
  },
  {
    id: 'hosp-helipad',
    name: 'Helikopter-Landeplatz',
    description: 'Symbolische Erweiterung — besseres Image, +1 Patientenkapazität.',
    cost: 150_000,
    upkeepPerDay: 4_000,
    buildTimeHours: 12,
    effects: { patientCapacity: 1 },
  },
];

/* Phase 10.1.3 — Polizei-Erweiterungen */
const POLICE_REVIER_EXTENSIONS: StationExtension[] = [
  {
    id: 'police-kennel',
    name: 'Diensthundezwinger',
    description: 'Ermöglicht das Stationieren von Diensthund-Modulen.',
    cost: 80_000,
    upkeepPerDay: 200,
    buildTimeHours: 8,
    effects: { allowsPoliceK9: true },
  },
  {
    id: 'police-workshop',
    name: 'Kfz-Werkstatt',
    description: 'Reparaturen können an dieser Wache durchgeführt werden.',
    cost: 40_000,
    upkeepPerDay: 800,
    buildTimeHours: 8,
    effects: { allowsRepair: true },
  },
  {
    id: 'police-holding-cells',
    name: 'Wachzelle',
    description: 'Erweitert die Wachzellen-Kapazität um 4 Plätze.',
    cost: 60_000,
    upkeepPerDay: 350,
    buildTimeHours: 12,
    effects: { holdingCells: 4 },
  },
];

const RIOT_POLICE_EXTENSIONS: StationExtension[] = [
  {
    id: 'riot-water-cannon-hall',
    name: 'Wasserwerfer-Halle',
    description: 'Pflicht zur Stationierung von WaWe-Fahrzeugen.',
    cost: 200_000,
    upkeepPerDay: 600,
    buildTimeHours: 24,
    effects: { allowsWaterCannon: true },
  },
];

const SEK_EXTENSIONS: StationExtension[] = [
  {
    id: 'sek-training-ground',
    name: 'Übungsgelände',
    description: 'Verkürzt SEK-Alarmierungszeit um 30 %.',
    cost: 300_000,
    upkeepPerDay: 1_200,
    buildTimeHours: 48,
    effects: { sekAlarmReduction: 0.3 },
  },
];

const FEDERAL_POLICE_EXTENSIONS: StationExtension[] = [
  {
    id: 'federal-helo-pad',
    name: 'Hubschrauber-Landeplatz',
    description: 'Pflicht zur Stationierung von Polizeihubschraubern.',
    cost: 350_000,
    upkeepPerDay: 1_500,
    buildTimeHours: 36,
    effects: { allowsPoliceHelo: true },
  },
];

export const STATION_EXTENSIONS: Record<StationKind, StationExtension[]> = {
  'ff-station': FF_EXTENSIONS,
  'bf-station': BF_EXTENSIONS,
  'ems-station': EMS_EXTENSIONS,
  hospital: HOSPITAL_EXTENSIONS,
  'police-station': POLICE_REVIER_EXTENSIONS,
  'riot-police-station': RIOT_POLICE_EXTENSIONS,
  'sek-base': SEK_EXTENSIONS,
  'federal-police-station': FEDERAL_POLICE_EXTENSIONS,
  courthouse: [],
};

/** Lookup any extension by id across all kinds. */
export function extensionById(id: string): StationExtension | null {
  for (const list of Object.values(STATION_EXTENSIONS)) {
    const m = list.find((x) => x.id === id);
    if (m) return m;
  }
  return null;
}

/**
 * Available extensions for a kind, excluding ones already installed.
 *
 * Phase 11.2.4 (Bug N7) — When the host station has `tier.builtInWorkshop`
 * (BF Tier ≥ 4), the workshop extension is filtered out: a duplicate is
 * pointless and the extension list previously offered it anyway.
 */
export function availableExtensions(
  kind: StationKind,
  installedIds: string[],
  tierHasBuiltInWorkshop = false,
): StationExtension[] {
  const installed = new Set(installedIds);
  return (STATION_EXTENSIONS[kind] ?? []).filter((x) => {
    if (installed.has(x.id)) return false;
    if (tierHasBuiltInWorkshop && x.effects.allowsRepair) return false;
    return true;
  });
}

/** Sum the vehicle-slot bonuses from all installed extensions. */
export function sumExtensionSlots(installedIds: string[]): number {
  let total = 0;
  for (const id of installedIds) {
    const ext = extensionById(id);
    if (ext?.effects.vehicleSlots) total += ext.effects.vehicleSlots;
  }
  return total;
}

/** Sum the patient-capacity bonuses (hospitals only). */
export function sumExtensionPatientCapacity(installedIds: string[]): number {
  let total = 0;
  for (const id of installedIds) {
    const ext = extensionById(id);
    if (ext?.effects.patientCapacity) total += ext.effects.patientCapacity;
  }
  return total;
}

/** Sum daily upkeep across installed extensions. */
export function sumExtensionUpkeep(installedIds: string[]): number {
  let total = 0;
  for (const id of installedIds) {
    const ext = extensionById(id);
    if (ext) total += ext.upkeepPerDay;
  }
  return total;
}

/** True if any installed extension grants the given boolean flag. */
export function hasExtensionFlag(
  installedIds: string[],
  flag:
    | 'allowsRepair'
    | 'allowsEmsVehicles'
    | 'allowsPoliceK9'
    | 'allowsWaterCannon'
    | 'allowsPoliceHelo',
): boolean {
  for (const id of installedIds) {
    const ext = extensionById(id);
    if (ext?.effects[flag]) return true;
  }
  return false;
}

/** Sum holding-cell additions across installed extensions. */
export function sumExtensionHoldingCells(installedIds: string[]): number {
  let total = 0;
  for (const id of installedIds) {
    const ext = extensionById(id);
    if (ext?.effects.holdingCells) total += ext.effects.holdingCells;
  }
  return total;
}

/** Phase 8.2 — Effective vehicle slots = tier slots + extension slots. */
export function effectiveVehicleSlots(
  station: Station,
  tierSlots: number,
): number {
  const extIds = (station.extensions ?? []).map((e) => e.extensionId);
  return tierSlots + sumExtensionSlots(extIds);
}
